import { CloudDownloadOutlined } from '@ant-design/icons'
import { Alert, Button, Select, SelectProps, Typography, message } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { Api } from '~/core/trpc'
import {
  FileType,
  MimeTypes,
} from '~/plugins/data-connector/providers/google-drive/common'
import { useOneDrivePicker } from '~/plugins/data-connector/providers/oneDrive/.client/components/useOneDrivePicker'
import { Extensions } from '~/plugins/data-connector/providers/oneDrive/common'
import { Toolbox } from '~/plugins/toolbox/shared'

const IconMimetype = (props: { item: FileType }) => {
  switch (props.item.mimeType) {
    case MimeTypes.FOLDER:
      return <i className="las la-folder" style={{ color: '#FFD700' }}></i>

    case MimeTypes.PDF:
      return <i className="las la-file-pdf" style={{ color: '#D32F2F' }}></i>

    case MimeTypes.DOCX:
      return <i className="las la-file-word" style={{ color: '#2A5599' }}></i>

    case MimeTypes.MSG:
      return <i className="las la-envelope" style={{ color: '#FF9800' }}></i>

    case MimeTypes.PNG:
    case MimeTypes.JPEG:
      return <i className="las la-file-image" style={{ color: '#4CAF50' }}></i>

    default:
      return <i className="las la-file" style={{ color: '#808080' }}></i>
  }
}

type Props = {
  id?: string
  value?: FileType[]
  initialValues?: FileType[]
  providerAccountId: string
  providerAccountScopeValues?: { scope: string }[]
  isMultiple?: boolean
  extensions?: Extensions[]
  buttonEmptyPlaceholder?: string
  formValuesConfiguration?: any
  onChange?: (value: FileType[]) => void
} & SelectProps

export const SelectOneDrivePicker: React.FC<Props> = ({
  id,
  value = [],
  initialValues,
  providerAccountId,
  providerAccountScopeValues = [],
  isMultiple = true,
  extensions,
  buttonEmptyPlaceholder,
  formValuesConfiguration,
  onChange,
  ...props
}) => {
  const [isLoading, setLoading] = useState(false)

  const accountQuery = Api.providerAccount.findUnique.useQuery(
    {
      where: { id: providerAccountId },
    },
    { enabled: !!providerAccountId },
  )

  const organizationMemberQuery = Api.organizationMember.findFirst.useQuery(
    {
      where: { userId: accountQuery.data?.userId },
    },
    { enabled: !!accountQuery.data?.userId },
  )

  const options = useMemo(() => {
    return value.map(item => {
      return {
        label: (
          <Typography.Text>
            <IconMimetype item={item} /> {item.name}
          </Typography.Text>
        ),
        value: JSON.stringify(item),
      }
    })
  }, [value])

  const optionsSelected = useMemo(() => {
    return options.map(option => option.value)
  }, [options])

  const ProviderOneDrivePickerPopup = useOneDrivePicker({
    providerAccountId,
    organizationMemberId: organizationMemberQuery.data?.id,
    isMultiple: true,
    extensions,
  })

  const handleStart = async () => {
    try {
      if (isLoading) {
        return
      }

      setLoading(true)

      ProviderOneDrivePickerPopup.openPopup()
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (values: string[]) => {
    const items = values.map(value => JSON.parse(value))

    onChange(items)
  }

  useEffect(() => {
    const items = ProviderOneDrivePickerPopup.pickedItems

    if (items?.length > 0) {
      const valuesUpdated = Toolbox.UtilityItems.makeUnique(
        [...value, ...items] as FileType[],
        'id',
      )

      onChange(valuesUpdated)
    }
  }, [ProviderOneDrivePickerPopup.pickedItems])

  const isBroken = !providerAccountId

  if (isBroken) {
    return (
      <span id={id}>
        <Alert
          type="warning"
          message={
            <Typography.Text type="warning">
              You must connect your account
            </Typography.Text>
          }
        />
      </span>
    )
  }

  const isEmpty = optionsSelected.length === 0

  return (
    <span id={id}>
      {isEmpty ? (
        <>
          <Button
            icon={<CloudDownloadOutlined />}
            loading={isLoading}
            onClick={handleStart}
          >
            {buttonEmptyPlaceholder ?? 'Select files'}
          </Button>
        </>
      ) : (
        <>
          <Select
            placeholder="Select a folder"
            {...props}
            loading={isLoading}
            value={optionsSelected}
            onChange={handleChange}
            onClear={() => onChange(null)}
            allowClear={true}
            mode="multiple"
            options={[
              ...options,
              isMultiple && {
                label: (
                  <Button
                    icon={<CloudDownloadOutlined />}
                    block
                    loading={isLoading}
                    onClick={handleStart}
                  >
                    Add files
                  </Button>
                ),
                value: 'create',
                disabled: true,
              },
            ].filter(Boolean)}
          />
        </>
      )}
    </span>
  )
}
